import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';
import groupService from '../../services/groupService';

// L'avviso al caricamento della decisione D2 di «il gruppo come ambito».
//
// Cosa NON fa, ed e' il punto: non blocca niente. `codex.create` resta senza ambito — chi
// digitalizza la posta in arrivo per piu' reparti carica su qualunque tipo — e questo
// componente non aggiunge alcun controllo. L'asimmetria «si carica cio' che non si vedra'»
// non viene pero' nascosta: e' l'unica cosa che compensa la liberta' del caricamento.
//
// I due testi dicono cose diverse perche' i due casi sono diversi, e con D1 il primo NON e'
// «non lo vedra' nessuno»:
//
//  - tipo ORFANO (in nessun gruppo): lo vedra' solo chi ha ambito Organizzazione. E' il
//    guasto di D1 visto da chi carica — il documento non sparisce, ma nessun reparto lo
//    lavorera'.
//  - tipo in gruppi, ma non nei miei: lo vedra' quel reparto e non io. E' una consegna, non
//    una perdita, e dirlo come una sparizione sarebbe falso.
export const TESTO_ORFANO =
    'Questo tipo non è in nessun gruppo: il documento sarà visibile solo a chi ha ambito ' +
    'Organizzazione, e nessun reparto lo troverà tra i propri. Chi amministra i gruppi può ' +
    'legarlo al reparto che deve lavorarlo.';

export const TESTO_NON_MIO =
    'Questo tipo non è in nessuno dei tuoi gruppi: potrai caricare il documento, ma non lo ' +
    'vedrai nei tuoi elenchi. Lo vedrà chi lavora nei gruppi a cui il tipo appartiene.';

/**
 * Carica una volta sola «i tipi dei miei gruppi» e dice, per un tipo scelto, quale dei due
 * avvisi mostrare — o nessuno.
 *
 * <p>Un fallimento della chiamata non e' un avviso e non e' un errore a schermo: e'
 * silenzio. Questa e' informazione accessoria su una schermata di caricamento, e un banner
 * rosso su un caricamento che funziona sposterebbe l'attenzione dal documento a una
 * diagnostica.</p>
 */
export function useMyCodexTypes() {
    const [stato, setStato] = useState(null);

    useEffect(() => {
        let vivo = true;
        groupService
            .mieiTipi()
            .then((dati) => {
                if (vivo) setStato(dati);
            })
            .catch(() => {
                // Silenzio voluto: vedi sopra.
                if (vivo) setStato(null);
            });
        return () => {
            vivo = false;
        };
    }, []);

    return stato;
}

/**
 * L'avviso per il tipo scelto, oppure `null`.
 *
 * @param {string} codexTypeId il tipo scelto da chi carica
 * @param {object|null} myCodexTypes la risposta di `groupService.mieiTipi()`
 */
export function avvisoPerTipo(codexTypeId, myCodexTypes) {
    if (!codexTypeId || !myCodexTypes) {
        return null;
    }
    const orfani = myCodexTypes.orphanCodexTypeIds || [];
    if (orfani.includes(codexTypeId)) {
        // L'orfano si avverte anche a chi ha ambito Organizzazione: per lui il documento
        // resta visibile, ma il fatto che nessun reparto lo lavorera' lo riguarda comunque —
        // anzi, e' l'unico che puo' rimediare.
        return TESTO_ORFANO;
    }
    if (myCodexTypes.unrestricted) {
        // Chi non e' ristretto a gruppi vede tutto: «non è nei tuoi gruppi» sarebbe vero
        // alla lettera e falso nella sostanza.
        return null;
    }
    const miei = myCodexTypes.codexTypeIds || [];
    return miei.includes(codexTypeId) ? null : TESTO_NON_MIO;
}

/** L'avviso reso, oppure niente. Non rende mai un riquadro vuoto. */
const CodexTypeScopeWarning = ({ codexTypeId, myCodexTypes, sx }) => {
    const testo = avvisoPerTipo(codexTypeId, myCodexTypes);
    if (!testo) {
        return null;
    }
    // «info» e non «warning»: non si sta sbagliando niente, e un triangolo giallo su un
    // caricamento legittimo insegna a ignorare i triangoli gialli.
    return (
        <Alert severity="info" sx={sx}>
            {testo}
        </Alert>
    );
};

CodexTypeScopeWarning.propTypes = {
    codexTypeId: PropTypes.string,
    myCodexTypes: PropTypes.shape({
        unrestricted: PropTypes.bool,
        codexTypeIds: PropTypes.arrayOf(PropTypes.string),
        orphanCodexTypeIds: PropTypes.arrayOf(PropTypes.string),
    }),
    sx: PropTypes.object,
};

export default CodexTypeScopeWarning;
